import { useEffect, useState } from "react";
import { fetchProtectedBlob } from "../api/files";

// ============================================================================
// Object URL condivisi per i file protetti sotto /uploads.
//
// `fetchProtectedBlob` scarica il file col token JWT e ne fa un blob. Finché
// il lavoro stava dentro `AuthedImage` ogni componente che mostrava lo stesso
// `file_url` lo riscaricava: con una sola miniatura per foto non si notava.
// Con la lightbox invece conta, perché la miniatura ha GIA' scaricato la foto
// a piena risoluzione — non esiste un endpoint di thumbnail, la griglia carica
// gli originali da qualche MB l'uno — e aprirla ingrandita deve riusare quel
// blob, non chiederlo una seconda volta.
//
// La cache e' per `file_url` e conta i riferimenti: l'object URL vive finche'
// almeno un componente lo mostra, e viene revocato quando l'ultimo si smonta.
// Serve il conteggio e non una cache che tiene tutto: chiudere il pannello di
// un sopralluogo con venti foto deve liberare davvero la memoria, invece di
// trattenerne decine di MB per il resto della sessione.
// ============================================================================

interface VoceCache {
  promise: Promise<string>;
  /** valorizzato solo a download concluso: e' l'URL da revocare al rilascio */
  objectUrl: string | null;
  riferimenti: number;
}

const cache = new Map<string, VoceCache>();

function acquisisci(src: string): Promise<string> {
  const esistente = cache.get(src);
  if (esistente) {
    esistente.riferimenti += 1;
    return esistente.promise;
  }

  const promise = fetchProtectedBlob(src).then((blob) => {
    const url = URL.createObjectURL(blob);
    const voce = cache.get(src);
    if (voce?.promise === promise) {
      voce.objectUrl = url;
    } else {
      // Tutti i lettori si sono smontati mentre il file arrivava: la voce non
      // e' piu' in cache, quindi nessun rilascio potrebbe piu' revocare questo
      // object URL. Va revocato subito qui, o resta appeso fino al reload.
      URL.revokeObjectURL(url);
    }
    return url;
  });

  // Ogni lettore attacca il proprio catch; questo serve solo a non far
  // risultare la promise "non gestita" quando il download fallisce.
  promise.catch(() => {});

  cache.set(src, { promise, objectUrl: null, riferimenti: 1 });
  return promise;
}

function rilascia(src: string): void {
  const voce = cache.get(src);
  if (!voce) return;

  voce.riferimenti -= 1;
  if (voce.riferimenti > 0) return;

  // Ultimo lettore andato: via dalla cache e via l'object URL. Una voce fallita
  // esce di qui come le altre, quindi il prossimo montaggio riprova il
  // download invece di ereditare l'errore per sempre.
  cache.delete(src);
  if (voce.objectUrl) URL.revokeObjectURL(voce.objectUrl);
}

/**
 * Scarica un file protetto di /uploads e ne restituisce un object URL pronto
 * per `<img src>`. Il download e' condiviso tra tutti i componenti montati
 * sullo stesso `src`: il secondo che lo chiede riusa il blob del primo.
 *
 * `src` null (o la foto che sparisce mentre e' aperta) e' un caso previsto:
 * restituisce semplicemente `objectUrl` null senza scaricare niente.
 */
export function useProtectedObjectUrl(src: string | null): {
  objectUrl: string | null;
  failed: boolean;
} {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setObjectUrl(null);
    setFailed(false);
    if (!src) return;

    let annullato = false;
    acquisisci(src)
      .then((url) => {
        if (!annullato) setObjectUrl(url);
      })
      .catch(() => {
        if (!annullato) setFailed(true);
      });

    return () => {
      annullato = true;
      rilascia(src);
    };
  }, [src]);

  return { objectUrl, failed };
}
